/**
 * ConversationContextStrip — faixa de contexto do celular (M1, spec §15).
 *
 * Segunda faixa do header mobile, logo abaixo da linha de 56px: CRM · Cliente ·
 * Tags em rolagem horizontal, na mesma ordem do computador. Só existe abaixo de
 * `lg` — no computador quem mostra isso são as colunas rotuladas.
 *
 * Conversa de GRUPO não renderiza a faixa: grupo não tem contato nem lead, e o
 * header volta aos 56px. Mesma checagem do ChatBox (`conversationType === 'group'`),
 * de propósito — se as duas divergirem, o computador mostra as colunas e o celular
 * esconde a faixa pra mesma conversa.
 *
 * ┌── Anatomia, verbatim do mockup M1 ──────────────────────────────────────────┐
 * │ faixa 48px · padding `0 10px 6px` · gap 7 · pílula visual de 36px           │
 * │ (`.mp` do mockup: raio 999, respiro `0 12px`, texto 13, peso 600).          │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * Alvo de toque: a pílula tem 36px, o dedo precisa de 44. A conta é
 * `36 + 4 + 4 = 44` — os 8px de respiro são padding DO BOTÃO, não margem, porque
 * margem não é clicável. E os filhos são alinhados pelo TOPO (`flex-start`), não
 * pelo centro: o miolo da faixa mede 48 − 6 = 42px, então um item de 44px
 * centralizado ficaria 1px acima da caixa e esse 1px sairia do clique
 * (`overflow` recorta O EVENTO junto com a pintura). Ancorado no topo, o botão
 * ocupa 0→44 dentro dos 48 da faixa: nada é recortado e o alvo é inteiro.
 *
 * Tipografia em px fixos da escala iOS (13 na pílula, 11 nos chips de tag) — regra
 * do projeto proíbe classe Tailwind de tamanho de texto no celular.
 *
 * Esmaecimento da borda direita: é `mask-image` no PRÓPRIO conteúdo rolável
 * (classe `.has-overflow`, em `index.css`), não um <div> de gradiente por cima.
 * Máscara reduz opacidade em vez de pintar — no tema escuro o token de fundo é
 * translúcido, e pintar somava uma segunda camada de tinta (retângulo mais escuro
 * com contorno, o oposto de esmaecer).
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import type { ConversationResponse } from '@troia-v3/types';

import { usePermissions } from '@/hooks/usePermissions';
import { Icon } from '@/shared/icons';
import { MOBILE_CONTEXT_STRIP_HEIGHT } from '@/shared/constants/layout';

import { useContactTags } from '../hooks';
import ConversationCustomerSelector from './ConversationCustomerSelector';
import ConversationStageSelector from './ConversationStageSelector';
import TagsDialog from './TagsDialog';

/** Alvo de toque mínimo da Apple. */
const TOUCH_TARGET = 44;
/** Altura visual da pílula (mockup M1, `.mp`). O respiro até os 44 é padding do botão. */
const PILL_HEIGHT = 36;
const PURPLE = '#8b5cf6';
/** Quantas tags cabem por extenso antes de virar "+N" (390px de tela). */
const MAX_VISIBLE_TAGS = 2;

interface ConversationContextStripProps {
  conversation: ConversationResponse;
}

const ConversationContextStrip: React.FC<ConversationContextStripProps> = ({ conversation }) => {
  const { hasModulePermission } = usePermissions();
  const canUpdateContact = hasModulePermission('contacts', 'update');
  const canReadCrm = hasModulePermission('crm', 'read');

  const contactId = conversation.contactId || conversation.contact?.id;
  const isGroup = conversation.conversationType === 'group';

  const [tagsOpen, setTagsOpen] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Mesma queryKey do ChatBox (`['contact', id, 'tags']`) — reusa cache, não refaz request.
  const { data: tags } = useContactTags(isGroup ? undefined : contactId);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const update = () => setHasOverflow(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [conversation.id, contactId, tags]);

  if (isGroup || !contactId) return null;

  const tagList = tags ?? [];
  const visibleTags = tagList.slice(0, MAX_VISIBLE_TAGS);
  const extraTags = tagList.length - visibleTags.length;
  const hasTags = tagList.length > 0;

  // Se os três campos estão fora (sem permissão nenhuma e sem dado pra ler), a
  // faixa não existe — 48px vazios abaixo do header pareceriam defeito.
  const showTags = hasTags || canUpdateContact;
  const showCustomer = !!conversation.contact?.customer || canUpdateContact;
  if (!canReadCrm && !showCustomer && !showTags) return null;

  // Cinza neutro, igual à pílula do Cliente (Task 14). O violeta é EXCLUSIVO da
  // pílula do CRM na faixa — tag violeta dilui o sinal (mesma decisão do computador).
  const tagsPillStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: PILL_HEIGHT,
    padding: '0 12px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: 220,
    ...(hasTags
      // Fundo mais fraco que o dos sub-chips: eles é que carregam o nome da tag,
      // a pílula é só o berço (mockup M1 — `.mtg` dentro de `.mp`).
      ? { color: 'var(--theme-card-text)', backgroundColor: 'rgba(148,163,184,0.10)', border: '1px solid var(--theme-card-border)' }
      : { color: PURPLE, backgroundColor: 'rgba(139,92,246,0.10)', border: '1px dashed rgba(139,92,246,0.45)' }),
  };

  /**
   * Sub-chip de tag DENTRO da pílula — `.mtg` do mockup M1: 26px de altura,
   * respiro `0 10px`, raio 999, peso 600. O texto é 11 (degrau da escala iOS;
   * o mockup usa 12, que não existe na escala do projeto) e é o mesmo tamanho
   * dos chips de tag do header do computador.
   */
  const tagChipStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    flex: '0 0 auto',
    height: 26,
    padding: '0 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: 'rgba(148,163,184,0.22)',
    color: 'var(--theme-card-text)',
    maxWidth: 92,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <div
      className="lg:hidden flex-shrink-0 border-b"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-card-background)' }}
      data-testid="chat-context-strip"
    >
      <div
        ref={scrollerRef}
        className={`chat-context-strip flex overflow-x-auto overflow-y-hidden${hasOverflow ? ' has-overflow' : ''}`}
        style={{
          height: MOBILE_CONTEXT_STRIP_HEIGHT,
          padding: '0 10px 6px',
          gap: 7,
          // Topo, não centro — ver o bloco "Alvo de toque" no cabeçalho do arquivo.
          alignItems: 'flex-start',
        }}
      >
        <ConversationStageSelector conversation={conversation} variant="mobile" />
        <ConversationCustomerSelector conversation={conversation} variant="mobile" />

        {showTags && (
          <button
            type="button"
            onClick={() => { if (canUpdateContact) setTagsOpen(true); }}
            disabled={!canUpdateContact}
            style={{
              height: TOUCH_TARGET,
              padding: '4px 0',
              background: 'transparent',
              border: 'none',
              flex: '0 0 auto',
              cursor: canUpdateContact ? 'pointer' : 'default',
            }}
            title={hasTags ? tagList.join(', ') : 'Adicionar tag'}
            aria-label="Gerenciar tags do contato"
            data-testid="chat-tags-trigger"
          >
            <span style={tagsPillStyle}>
              <Icon name={hasTags ? 'tag' : 'plus'} className="h-3.5 w-3.5" />
              {hasTags ? (
                <>
                  {visibleTags.map((tag) => (
                    <span key={tag} style={tagChipStyle}>{tag}</span>
                  ))}
                  {extraTags > 0 && (
                    <span style={{ ...tagChipStyle, fontWeight: 700 }}>+{extraTags}</span>
                  )}
                </>
              ) : 'Tag'}
            </span>
          </button>
        )}
      </div>

      <TagsDialog
        contactId={contactId}
        contactName={conversation.contact?.name}
        isOpen={tagsOpen}
        onClose={() => setTagsOpen(false)}
      />
    </div>
  );
};

export default ConversationContextStrip;
export { ConversationContextStrip };
